<template>
  <div class="gl-form-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <router-link to="/owner/transfers">Transfers</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Create</span>
          </div>
          <h1 class="gl-branch-title">Create Stock Transfer</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="submitForm">
        <div class="gl-form-layout">

          <!-- ── Main form ── -->
          <div class="gl-form-card">

            <!-- Section: Transfer Details -->
            <div class="gl-section">
              <div class="gl-section-header">
                <div class="gl-section-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                </div>
                <span class="gl-section-title">Transfer Details</span>
              </div>
              <div class="gl-form-row">
                <div class="gl-form-group">
                  <label class="gl-label">
                    From Branch <span class="gl-required">*</span>
                    <span class="gl-label-hint">Branch sending the stock</span>
                  </label>
                  <select
                    class="gl-select"
                    :class="{ 'gl-select-error': errors.source_outlet_id }"
                    v-model="form.source_outlet_id"
                    @change="loadSourceProducts"
                    required
                  >
                    <option value="">Select Source Branch</option>
                    <option
                      v-for="branch in branches"
                      :key="branch.id"
                      :value="branch.id"
                      :disabled="branch.id === form.destination_outlet_id"
                    >{{ branch.name }}</option>
                  </select>
                  <div v-if="errors.source_outlet_id" class="gl-error-msg">{{ errors.source_outlet_id[0] }}</div>
                </div>
                <div class="gl-form-group">
                  <label class="gl-label">
                    To Branch <span class="gl-required">*</span>
                    <span class="gl-label-hint">Branch receiving the stock</span>
                  </label>
                  <select
                    class="gl-select"
                    :class="{ 'gl-select-error': errors.destination_outlet_id }"
                    v-model="form.destination_outlet_id"
                    required
                  >
                    <option value="">Select Destination Branch</option>
                    <option
                      v-for="branch in branches"
                      :key="branch.id"
                      :value="branch.id"
                      :disabled="branch.id === form.source_outlet_id"
                    >{{ branch.name }}</option>
                  </select>
                  <div v-if="errors.destination_outlet_id" class="gl-error-msg">{{ errors.destination_outlet_id[0] }}</div>
                </div>
              </div>
            </div>

            <!-- Section: Products -->
            <div class="gl-section">
              <div class="gl-section-header">
                <div class="gl-section-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                </div>
                <span class="gl-section-title">Products to Transfer</span>
              </div>

              <!-- No source branch selected -->
              <div v-if="!form.source_outlet_id" class="gl-info-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Select a source branch above to load available products.
              </div>

              <!-- Loading products -->
              <div v-else-if="loadingProducts" class="gl-loading-inline">
                <div class="gl-spinner-sm"></div>
                <span>Loading products…</span>
              </div>

              <div v-else>
                <!-- Product picker row -->
                <div class="gl-product-picker">
                  <select class="gl-select gl-product-select" v-model="selectedProductId">
                    <option value="">Select a product to add…</option>
                    <option
                      v-for="product in availableProducts"
                      :key="product.id"
                      :value="product.id"
                      :disabled="isProductAdded(product.id)"
                    >{{ product.name }} — {{ product.pivot?.stock || 0 }} in stock</option>
                  </select>
                  <input
                    type="number"
                    class="gl-input gl-qty-input"
                    v-model="selectedQuantity"
                    min="1"
                    placeholder="Qty"
                  >
                  <button
                    type="button"
                    class="gl-add-product-btn"
                    @click="addProduct"
                    :disabled="!selectedProductId || !selectedQuantity"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add
                  </button>
                </div>

                <!-- Added items table -->
                <div v-if="form.items.length > 0" class="gl-items-wrap">
                  <table class="gl-items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th class="th-center">Available</th>
                        <th class="th-center">Qty</th>
                        <th>Notes</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in form.items" :key="index">
                        <td>
                          <div class="gl-item-name">{{ getProductName(item.product_id) }}</div>
                        </td>
                        <td class="td-center">
                          <span class="gl-avail-badge">{{ getProductStock(item.product_id) }}</span>
                        </td>
                        <td class="td-qty">
                          <input
                            type="number"
                            class="gl-input gl-input-sm"
                            v-model="item.quantity"
                            min="1"
                            :max="getProductStock(item.product_id)"
                          >
                        </td>
                        <td>
                          <input
                            type="text"
                            class="gl-input gl-input-sm"
                            v-model="item.notes"
                            placeholder="Optional notes"
                          >
                        </td>
                        <td class="td-remove">
                          <button type="button" class="gl-remove-btn" @click="removeProduct(index)" title="Remove">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- No items yet -->
                <div v-else class="gl-items-empty">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  No products added yet. Use the picker above to add products.
                </div>
              </div>
            </div>

            <!-- Section: Notes -->
            <div class="gl-section">
              <div class="gl-section-header">
                <div class="gl-section-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                </div>
                <span class="gl-section-title">Transfer Notes</span>
                <span class="gl-section-optional">Optional</span>
              </div>
              <div class="gl-form-group">
                <textarea
                  class="gl-textarea"
                  v-model="form.notes"
                  rows="2"
                  placeholder="e.g. Urgent restock for weekend promotion, Monthly inventory balance…"
                ></textarea>
              </div>
            </div>

            <!-- Form footer (desktop) -->
            <div class="gl-form-footer">
              <button
                type="submit"
                class="gl-submit-btn"
                :disabled="submitting || form.items.length === 0"
              >
                <span v-if="submitting" class="gl-btn-spinner"></span>
                <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                Create Transfer
              </button>
              <router-link to="/owner/transfers" class="gl-cancel-link">Cancel</router-link>
              <span v-if="form.items.length === 0 && form.source_outlet_id" class="gl-footer-hint">
                Add at least one product to continue
              </span>
            </div>

            <!-- Sticky bottom bar (mobile) -->
            <div class="gl-sticky-bar">
              <router-link to="/owner/transfers" class="gl-sticky-cancel">Cancel</router-link>
              <button
                type="submit"
                class="gl-sticky-submit"
                :disabled="submitting || form.items.length === 0"
              >
                <span v-if="submitting" class="gl-btn-spinner"></span>
                Create Transfer
              </button>
            </div>

          </div>

          <!-- ── Summary sidebar ── -->
          <div class="gl-form-sidebar">
            <div class="gl-summary-card">
              <div class="gl-summary-header">
                <div class="gl-summary-title-row">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  <span class="gl-summary-title">Transfer Summary</span>
                </div>
              </div>
              <div class="gl-summary-body">

                <!-- Route visualization -->
                <div class="gl-summary-route">
                  <div class="gl-route-end">
                    <div class="gl-route-label">From</div>
                    <div class="gl-route-name" :class="{ empty: !form.source_outlet_id }">
                      {{ getSourceBranchName() || 'Not selected' }}
                    </div>
                  </div>
                  <div class="gl-route-arrow">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </div>
                  <div class="gl-route-end">
                    <div class="gl-route-label">To</div>
                    <div class="gl-route-name" :class="{ empty: !form.destination_outlet_id }">
                      {{ getDestinationBranchName() || 'Not selected' }}
                    </div>
                  </div>
                </div>

                <div class="gl-summary-divider"></div>

                <div class="gl-summary-stats">
                  <div class="gl-summary-stat">
                    <span class="gl-summary-stat-label">Total Items</span>
                    <span class="gl-summary-stat-value">{{ form.items.length }} <span class="gl-summary-stat-unit">product{{ form.items.length !== 1 ? 's' : '' }}</span></span>
                  </div>
                  <div class="gl-summary-stat">
                    <span class="gl-summary-stat-label">Total Quantity</span>
                    <span class="gl-summary-stat-value">{{ getTotalQuantity() }} <span class="gl-summary-stat-unit">unit{{ getTotalQuantity() !== 1 ? 's' : '' }}</span></span>
                  </div>
                </div>

                <!-- Items preview list -->
                <div class="gl-summary-items" v-if="form.items.length > 0">
                  <div class="gl-summary-items-label">Items</div>
                  <div class="gl-summary-item-row" v-for="(item, i) in form.items" :key="i">
                    <span class="gl-summary-item-name">{{ getProductName(item.product_id) }}</span>
                    <span class="gl-summary-item-qty">× {{ item.quantity }}</span>
                  </div>
                </div>

              </div>
            </div>

            <!-- Tips card -->
            <div class="gl-tips-card">
              <div class="gl-tips-title">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Tips
              </div>
              <ul class="gl-tips-list">
                <li>Select the source branch first to load available products</li>
                <li>Quantity cannot exceed available stock at source branch</li>
                <li>Transfer requires approval before stock is moved</li>
              </ul>
            </div>
          </div>

        </div>
      </form>
    </div>

  </div>
</template>

<script>
import { ownerTransferApi, ownerBranchApi, ownerStockApi } from '@/services/ownerApi';

export default {
  name: 'TransferForm',

  data() {
    return {
      loading: false,
      loadingProducts: false,
      submitting: false,
      errors: {},
      branches: [],
      sourceProducts: [],
      selectedProductId: '',
      selectedQuantity: 1,
      form: {
        source_outlet_id: '',
        destination_outlet_id: '',
        items: [],
        notes: ''
      }
    };
  },

  computed: {
    availableProducts() {
      return this.sourceProducts.filter(p => (p.pivot?.stock || 0) > 0);
    }
  },

  mounted() {
    this.fetchBranches();
  },

  methods: {
    async fetchBranches() {
      try {
        const response = await ownerBranchApi.list({ per_page: 100 });
        this.branches = response.data.data;
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    },

    async loadSourceProducts() {
      if (!this.form.source_outlet_id) {
        this.sourceProducts = [];
        return;
      }
      try {
        this.loadingProducts = true;
        const response = await ownerStockApi.list(this.form.source_outlet_id, { per_page: 100 });
        this.sourceProducts = response.data.data;
        this.form.items = [];
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        this.loadingProducts = false;
      }
    },

    isProductAdded(productId) {
      return this.form.items.some(item => item.product_id === productId);
    },

    getProductName(productId) {
      const product = this.sourceProducts.find(p => p.id === productId);
      return product?.name || 'Unknown';
    },

    getProductStock(productId) {
      const product = this.sourceProducts.find(p => p.id === productId);
      return product?.pivot?.stock || 0;
    },

    getSourceBranchName() {
      const branch = this.branches.find(b => b.id === this.form.source_outlet_id);
      return branch?.name;
    },

    getDestinationBranchName() {
      const branch = this.branches.find(b => b.id === this.form.destination_outlet_id);
      return branch?.name;
    },

    getTotalQuantity() {
      return this.form.items.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
    },

    addProduct() {
      if (!this.selectedProductId || !this.selectedQuantity) return;
      const product = this.sourceProducts.find(p => p.id === parseInt(this.selectedProductId));
      if (!product) return;
      const maxStock = product.pivot?.stock || 0;
      const quantity = Math.min(parseInt(this.selectedQuantity), maxStock);
      this.form.items.push({ product_id: product.id, quantity, notes: '' });
      this.selectedProductId = '';
      this.selectedQuantity = 1;
    },

    removeProduct(index) {
      this.form.items.splice(index, 1);
    },

    async submitForm() {
      if (this.form.items.length === 0) {
        alert('Please add at least one product to transfer');
        return;
      }
      try {
        this.submitting = true;
        this.errors = {};
        await ownerTransferApi.create(this.form);
        this.$router.push('/owner/transfers');
      } catch (error) {
        console.error('Failed to create transfer:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          alert(error.response?.data?.message || 'Failed to create transfer');
        }
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page header ── */
.gl-branch-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-branch-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }

/* ── Two-column layout ── */
.gl-form-layout {
  display: grid;
  grid-template-columns: 1fr 288px;
  gap: 20px;
  align-items: start;
  margin-bottom: 40px;
}

/* ── Main form card ── */
.gl-form-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px; overflow: hidden; }
.gl-section { padding: 22px 24px; border-bottom: 1px solid #f5f6f8; }
.gl-section:last-child { border-bottom: none; }

.gl-section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.gl-section-icon { width: 28px; height: 28px; border-radius: 7px; background: #dff0ec; color: #900C27; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-section-title { font-size: 13.5px; font-weight: 700; color: #111827; }
.gl-section-optional { font-size: 11px; color: #9ca3af; background: #f3f4f6; padding: 2px 7px; border-radius: 20px; margin-left: 4px; }

/* Form row + group */
.gl-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.gl-form-group { display: flex; flex-direction: column; gap: 5px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.gl-label-hint { font-size: 11px; color: #9ca3af; font-weight: 400; }
.gl-required { color: #dc2626; }
.gl-error-msg { font-size: 12px; color: #dc2626; margin-top: 2px; }

/* Form elements */
.gl-select { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 9px 12px; font-size: 13.5px; color: #111827; background: #fff; width: 100%; box-sizing: border-box; outline: none; cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
.gl-select:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.1); }
.gl-select-error { border-color: #fca5a5; }
.gl-input { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 9px 12px; font-size: 13.5px; color: #111827; background: #fff; width: 100%; box-sizing: border-box; outline: none; transition: border-color 0.15s, box-shadow 0.15s; }
.gl-input:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.1); }
.gl-input-sm { padding: 6px 9px; font-size: 12.5px; border-radius: 7px; }
.gl-textarea { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 9px 12px; font-size: 13.5px; color: #111827; background: #fff; width: 100%; box-sizing: border-box; outline: none; resize: vertical; transition: border-color 0.15s, box-shadow 0.15s; }
.gl-textarea:focus { border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,0.1); }

/* Info box (no source) */
.gl-info-box { display: flex; align-items: center; gap: 10px; padding: 13px 16px; background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 10px; font-size: 13.5px; color: #2563eb; }

/* Loading inline */
.gl-loading-inline { display: flex; align-items: center; gap: 10px; padding: 20px 0; font-size: 13.5px; color: #9ca3af; }
.gl-spinner-sm { width: 16px; height: 16px; border: 2px solid #f0f1f3; border-top-color: #900C27; border-radius: 50%; animation: gl-spin 0.7s linear infinite; flex-shrink: 0; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* Product picker */
.gl-product-picker { display: flex; gap: 8px; align-items: center; }
.gl-product-select { flex: 1; }
.gl-qty-input { width: 88px !important; flex-shrink: 0; padding: 9px 10px; text-align: center; }
.gl-add-product-btn {
  padding: 9px 16px; border-radius: 9px; background: #900C27; color: #fff;
  border: none; font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 6px; flex-shrink: 0; white-space: nowrap;
  transition: opacity 0.15s;
}
.gl-add-product-btn:hover:not(:disabled) { opacity: 0.88; }
.gl-add-product-btn:disabled { opacity: 0.42; cursor: not-allowed; }

/* Items table */
.gl-items-wrap { margin-top: 14px; border: 1.5px solid #f0f1f3; border-radius: 10px; overflow: hidden; }
.gl-items-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.gl-items-table thead th { padding: 9px 12px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; background: #f8f9fa; border-bottom: 1px solid #f0f1f3; }
.gl-items-table thead th.th-center { text-align: center; }
.gl-items-table tbody td { padding: 9px 12px; border-bottom: 1px solid #f5f6f8; vertical-align: middle; color: #374151; }
.gl-items-table tbody tr:last-child td { border-bottom: none; }
.gl-items-table tbody td.td-center { text-align: center; }
.gl-items-table tbody td.td-qty { width: 100px; }
.gl-items-table tbody td.td-remove { width: 40px; text-align: center; }
.gl-item-name { font-weight: 600; color: #111827; font-size: 13px; }
.gl-avail-badge { display: inline-flex; align-items: center; justify-content: center; padding: 2px 9px; background: #f3f4f6; color: #6b7280; font-size: 12px; font-weight: 600; border-radius: 20px; }
.gl-remove-btn { width: 28px; height: 28px; border-radius: 6px; border: 1.5px solid #fecaca; background: #fff5f5; color: #dc2626; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
.gl-remove-btn:hover { background: #fee2e2; }

/* Empty items state */
.gl-items-empty { display: flex; align-items: center; gap: 10px; padding: 13px 16px; background: #fffbf0; border: 1.5px solid #fde68a; border-radius: 10px; font-size: 13.5px; color: #d97706; margin-top: 12px; }

/* Form footer */
.gl-form-footer { display: flex; align-items: center; gap: 12px; padding: 20px 24px; }
.gl-submit-btn {
  padding: 10px 22px; background: #900C27; color: #fff;
  border: none; border-radius: 10px; font-size: 13.5px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
  transition: opacity 0.15s, box-shadow 0.15s;
}
.gl-submit-btn:hover:not(:disabled) { opacity: 0.88; }
.gl-submit-btn:disabled { opacity: 0.42; cursor: not-allowed; box-shadow: none; }
.gl-cancel-link { padding: 10px 18px; border: 1.5px solid #e8eaf0; border-radius: 10px; font-size: 13.5px; font-weight: 600; color: #6b7280; text-decoration: none; background: #fff; transition: background 0.15s; }
.gl-cancel-link:hover { background: #f3f4f6; color: #374151; text-decoration: none; }
.gl-footer-hint { font-size: 12px; color: #9ca3af; }
.gl-btn-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: gl-spin 0.7s linear infinite; flex-shrink: 0; }

/* ── Summary sidebar ── */
.gl-form-sidebar { position: sticky; top: 24px; display: flex; flex-direction: column; gap: 14px; }

.gl-summary-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px; overflow: hidden; }
.gl-summary-header { padding: 14px 18px; border-bottom: 1px solid #f5f6f8; }
.gl-summary-title-row { display: flex; align-items: center; gap: 8px; color: #9ca3af; }
.gl-summary-title { font-size: 13px; font-weight: 700; color: #111827; }
.gl-summary-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 14px; }

.gl-summary-route { display: flex; align-items: center; gap: 10px; padding: 12px 14px; background: #f8f9fb; border-radius: 10px; border: 1.5px solid #f0f1f3; }
.gl-route-end { flex: 1; min-width: 0; }
.gl-route-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: #9ca3af; font-weight: 600; margin-bottom: 2px; }
.gl-route-name { font-size: 13px; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-route-name.empty { color: #c0c4cc; font-weight: 400; font-style: italic; }
.gl-route-arrow { flex-shrink: 0; color: #900C27; }

.gl-summary-divider { height: 1px; background: #f0f1f3; }
.gl-summary-stats { display: flex; flex-direction: column; gap: 10px; }
.gl-summary-stat { display: flex; justify-content: space-between; align-items: baseline; }
.gl-summary-stat-label { font-size: 12.5px; color: #6b7280; }
.gl-summary-stat-value { font-size: 15px; font-weight: 700; color: #111827; }
.gl-summary-stat-unit { font-size: 11.5px; font-weight: 400; color: #9ca3af; }

.gl-summary-items { display: flex; flex-direction: column; gap: 4px; }
.gl-summary-items-label { font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; margin-bottom: 4px; }
.gl-summary-item-row { display: flex; justify-content: space-between; align-items: center; padding: 5px 10px; background: #f8f9fb; border-radius: 7px; }
.gl-summary-item-name { font-size: 12.5px; color: #374151; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-right: 8px; }
.gl-summary-item-qty { font-size: 12px; font-weight: 700; color: #900C27; flex-shrink: 0; }

/* Tips card */
.gl-tips-card { background: #f8f9fb; border: 1.5px solid #f0f1f3; border-radius: 14px; padding: 14px 16px; }
.gl-tips-title { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700; color: #6b7280; margin-bottom: 10px; }
.gl-tips-list { margin: 0; padding: 0 0 0 16px; display: flex; flex-direction: column; gap: 6px; }
.gl-tips-list li { font-size: 12px; color: #9ca3af; line-height: 1.4; }

/* ── Sticky bar (mobile only) ── */
.gl-sticky-bar { display: none; position: fixed; bottom: 0; left: 0; right: 0; z-index: 100; background: #fff; border-top: 1.5px solid #f0f1f3; padding: 12px 16px; gap: 10px; box-shadow: 0 -4px 20px rgba(0,0,0,0.06); }
.gl-sticky-cancel { flex: 0 0 auto; padding: 11px 18px; border: 1.5px solid #e8eaf0; border-radius: 10px; font-size: 14px; font-weight: 600; color: #6b7280; text-decoration: none; background: #fff; display: flex; align-items: center; justify-content: center; }
.gl-sticky-submit { flex: 1; padding: 11px 18px; background: #900C27; color: #fff; border: none; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
.gl-sticky-submit:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-form-page { padding-bottom: 90px; }
  .gl-branch-title { font-size: 18px; }

  /* Single column layout, hide sidebar */
  .gl-form-layout { grid-template-columns: 1fr !important; gap: 0; }
  .gl-form-sidebar { display: none !important; }

  /* Section spacing */
  .gl-section { padding: 16px !important; }

  /* From/To branch — stack */
  .gl-form-row { grid-template-columns: 1fr !important; gap: 12px; }

  /* Product picker — wrap to 2 rows */
  .gl-product-picker { flex-wrap: wrap; gap: 8px; }
  .gl-product-select { width: 100% !important; flex: none !important; order: 1; }
  .gl-qty-input { flex: 1 !important; width: auto !important; order: 2; }
  .gl-add-product-btn { order: 3; }

  /* Items table — hide Available (col 2) + Notes (col 4) */
  .gl-items-table thead th:nth-child(2),
  .gl-items-table tbody td:nth-child(2),
  .gl-items-table thead th:nth-child(4),
  .gl-items-table tbody td:nth-child(4) { display: none; }
  .gl-items-table { font-size: 12.5px; }

  /* Form footer hidden on mobile, sticky bar shown */
  .gl-form-footer { display: none !important; }
  .gl-sticky-bar { display: flex !important; }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-branch-title { color: #f3f4f6; }
body.dark-only .gl-branch-breadcrumb { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #d1d5db; }

body.dark-only .gl-form-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-section { border-color: #2d3249; }
body.dark-only .gl-section-title { color: #f3f4f6; }
body.dark-only .gl-section-icon { background: rgba(144,12,39,0.2); color: #4ade80; }
body.dark-only .gl-section-optional { background: #2d3249; color: #6b7280; }
body.dark-only .gl-label { color: #d1d5db; }
body.dark-only .gl-select { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-input { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-textarea { border-color: #2d3249; background: #262932; color: #e5e7eb; }
body.dark-only .gl-info-box { background: rgba(37,99,235,0.1); border-color: rgba(37,99,235,0.3); color: #93c5fd; }
body.dark-only .gl-items-empty { background: rgba(217,119,6,0.08); border-color: rgba(217,119,6,0.25); color: #fbbf24; }
body.dark-only .gl-items-wrap { border-color: #2d3249; }
body.dark-only .gl-items-table thead th { background: #252837; color: #6b7280; border-color: #2d3249; }
body.dark-only .gl-items-table tbody td { border-color: #2a2f46; color: #e5e7eb; }
body.dark-only .gl-item-name { color: #f3f4f6; }
body.dark-only .gl-avail-badge { background: #2d3249; color: #9ca3af; }
body.dark-only .gl-form-footer { border-color: #2d3249; }
body.dark-only .gl-cancel-link { border-color: #2d3249; background: #262932; color: #9ca3af; }
body.dark-only .gl-cancel-link:hover { background: #2d3249; color: #d1d5db; }
body.dark-only .gl-footer-hint { color: #6b7280; }

body.dark-only .gl-summary-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-summary-header { border-color: #2d3249; }
body.dark-only .gl-summary-title { color: #f3f4f6; }
body.dark-only .gl-summary-route { background: #262932; border-color: #2d3249; }
body.dark-only .gl-route-label { color: #6b7280; }
body.dark-only .gl-route-name { color: #f3f4f6; }
body.dark-only .gl-summary-divider { background: #2d3249; }
body.dark-only .gl-summary-stat-label { color: #9ca3af; }
body.dark-only .gl-summary-stat-value { color: #f3f4f6; }
body.dark-only .gl-summary-item-row { background: #262932; }
body.dark-only .gl-summary-item-name { color: #d1d5db; }
body.dark-only .gl-tips-card { background: #262932; border-color: #2d3249; }
body.dark-only .gl-tips-title { color: #9ca3af; }
body.dark-only .gl-tips-list li { color: #6b7280; }
body.dark-only .gl-sticky-bar { background: #1d1e26 !important; border-top-color: #323644 !important; }
body.dark-only .gl-sticky-cancel { background: #262932 !important; border-color: #2d3249 !important; color: #9ca3af !important; }
</style>
